import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ChatConnectedAccountPayload, ChatConnectedAccountPlatform, Message } from '@/types/chat';
import { ChatApiService } from '@/services/chatApiService';
import { MESSAGE_RECOVERY_MIN_AGE_MS } from '../constants';
import { useConnectedAccounts } from '@/hooks/use-connected-accounts';

// Import all the specialized hooks
import { useChatSessions } from './useChatSessions';
import { useChatMessages } from './useChatMessages';
import { useChatActions } from './useChatActions';
import { useChatUI } from './useChatUI';
import { useMessageEditing } from './useMessageEditing';
import { useChatFiles } from './useChatFiles';
import { ERROR_CODES } from '@/lib/dify/errorUtils';

// The props for the master hook are just the top-level config
export interface UseChatProps {
  chatApiService: ChatApiService;
  userId: string | null;
  isUserLoading: boolean;
  handleRateLimitError?: (error: any, response?: Response) => Promise<boolean>;
}

export function useChat({ chatApiService, userId, isUserLoading, handleRateLimitError }: UseChatProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  // State for holding a message that comes from outside the chat context (e.g., landing page)
  const [initialMessage, setInitialMessage] = useState<string | null>(null);

  // Resolve connected accounts for structured payload
  const { accounts } = useConnectedAccounts();

  const connectedAccountPayload = useMemo<ChatConnectedAccountPayload | undefined>(() => {
    const mapped = accounts
      .filter((account) => {
        const status = (account.status || '').toLowerCase();
        const connectionType = (account.connection_type || '').toLowerCase();

        // Filter: only active accounts
        if (status !== 'active') return false;

        // Filter: exclude Threads accounts (threads_via_instagram)
        if (connectionType.includes('threads')) return false;

        return true;
      })
      .map((account) => {
        const connectionType = (account.connection_type || '').toLowerCase();
        const platform: ChatConnectedAccountPlatform | null = connectionType.includes('instagram')
          ? 'instagram'
          : connectionType.includes('tiktok')
            ? 'tiktok'
            : connectionType.includes('youtube')
              ? 'youtube'
              : null;

        if (!platform) {
          return null;
        }

        const accountId = String(account.connected_social_account_id || account.id || '').trim();
        const username = String(account.username || '').trim();
        return {
          platform,
          connected_social_account_id: accountId || 'none',
          username: username || 'none',
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

    if (mapped.length === 0) {
      return { accounts: [] };
    }

    return { accounts: mapped };
  }, [accounts]);

  // 1. Instantiate all the specialized hooks
  const sessions = useChatSessions(chatApiService, { userId, isUserLoading });
  const activeChat = sessions.chatSessions.find(chat => chat.id === sessions.activeChatId);

  const messages = useChatMessages({
    activeChat,
    userId: sessions.userId,
    chatApiService,
    setChatSessions: sessions.setChatSessions,
    updateChatSession: sessions.updateChatSession,
  });

  const ui = useChatUI();
  const files = useChatFiles({ userId: sessions.userId, chatApiService });

  const handleFileUpload = useCallback(async (filesToUpload: FileList | File[] | null) => {
    if (!filesToUpload || filesToUpload.length === 0) {
      console.log('[useChat] handleFileUpload exiting: no files provided');
      return;
    }

    let targetChatId = sessions.activeChatId;
    if (!targetChatId) {
      const newChatId = await sessions.createNewChat();
      if (!newChatId) {
        return;
      }
      targetChatId = newChatId;
    }

    await files.handleFileSelect(filesToUpload, targetChatId);
  }, [files, sessions]);

  const actions = useChatActions({
    userId: sessions.userId,
    activeChat,
    chatApiService,
    chatSessionsRef: sessions.chatSessionsRef,
    setChatSessions: sessions.setChatSessions,
    updateChatSession: sessions.updateChatSession,
    saveConversationsToCache: sessions.saveConversationsToCache,
    createNewChat: sessions.createNewChat,
    addOptimisticMessage: messages.addOptimisticMessage,
    updateOptimisticMessage: messages.updateOptimisticMessage,
    removeMessagesAfterIndex: messages.removeMessagesAfterIndex,
    saveMessagesToCache: messages.saveMessagesToCache,
    selectedFiles: files.selectedFiles,
    setSelectedFiles: files.setSelectedFiles,
    setIsUploading: files.setIsUploading,
    inputMessage: ui.inputMessage,
    setInputMessage: ui.setInputMessage,
    replyingToMessage: ui.replyingToMessage,
    cancelReply: ui.cancelReply,
    handleRateLimitError, // Pass down from ChatProvider
    connectedAccountPayload,
  });

  const editing = useMessageEditing({
    isResponding: actions.isResponding,
    // When editing starts, cancel any active reply
    onStartEdit: ui.cancelReply,
    // Wire up the save action to the function from useChatActions
    onSaveEdit: actions.handleSaveEditAction,
  });

  // Auto-recovery effect: Check for incomplete messages and try to refresh from Dify
  const recoveredMessageIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!activeChat || !activeChat.difyConversationId || actions.isResponding) {
      return;
    }

    const now = Date.now();

    // 1. Find assistant messages with error flags (existing pattern)
    const assistantMessagesWithErrors = activeChat.messages.filter(
      msg => msg.role === 'assistant' &&
             msg.isDifyAgentError &&
             !recoveredMessageIds.current.has(msg.id)
    );

    // 2. Find orphaned user messages (new pattern)
    const orphanedUserMessages = activeChat.messages.filter((msg, index) => {
      // Only check user messages
      if (msg.role !== 'user') return false;

      // Skip if already attempted recovery
      if (recoveredMessageIds.current.has(msg.id)) return false;

      // Skip if message is too recent (might still be processing)
      const messageAge = now - msg.timestamp.getTime();
      if (messageAge < MESSAGE_RECOVERY_MIN_AGE_MS) return false;

      // Check if there's any assistant message after this user message
      const hasFollowingAssistant = activeChat.messages
        .slice(index + 1)
        .some(m => m.role === 'assistant');

      // If no following assistant, this is orphaned
      return !hasFollowingAssistant;
    });

    const messagesToRecover = [
      ...assistantMessagesWithErrors,
      ...orphanedUserMessages
    ];

    if (messagesToRecover.length === 0) {
      return;
    }

    // Attempt recovery for each incomplete message
    messagesToRecover.forEach(msg => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Auto-Recovery] Attempting to recover incomplete message', {
          messageId: msg.id,
          messageRole: msg.role,
          chatId: activeChat.id,
          difyConversationId: activeChat.difyConversationId,
        });
      }

      // Mark as attempted before making the request to avoid duplicate attempts
      recoveredMessageIds.current.add(msg.id);

      if (msg.role === 'assistant') {
        // Existing flow: Update the assistant message
        actions.refreshConversationAfterError(
          activeChat.id,
          msg.id,
          activeChat.difyConversationId
        ).then(recovered => {
          if (process.env.NODE_ENV === 'development') {
            console.log('[Auto-Recovery] Assistant message recovery result', {
              messageId: msg.id,
              recovered,
            });
          }
        }).catch(err => {
          console.error('[Auto-Recovery] Assistant message recovery error:', err);
        });
      } else if (msg.role === 'user') {
        // New flow: Create assistant message, then recover into it
        const newAssistantId = `msg-${uuidv4()}-recovered`;

        if (process.env.NODE_ENV === 'development') {
          console.log('[Auto-Recovery] Creating new assistant message for orphaned user', {
            userMessageId: msg.id,
            newAssistantId,
          });
        }

        // Add empty assistant message optimistically
        messages.addOptimisticMessage(activeChat.id, {
          id: newAssistantId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          fileIds: [],
          feedback: null,
          isThinking: true, // Show as loading during recovery
        });

        // Attempt recovery
        actions.refreshConversationAfterError(
          activeChat.id,
          newAssistantId,
          activeChat.difyConversationId,
          msg.id // Pass the user message ID for matching
        ).then(recovered => {
          if (process.env.NODE_ENV === 'development') {
            console.log('[Auto-Recovery] Orphaned user message recovery result', {
              userMessageId: msg.id,
              newAssistantId,
              recovered,
            });
          }

          if (!recovered) {
            // If recovery fails, mark the message with an error
            messages.updateOptimisticMessage(activeChat.id, newAssistantId, {
              content: '',
              isThinking: false,
              errorMessage: t(ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE),
              errorCode: ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE,
              isDifyAgentError: true,
            });
          }
        }).catch(err => {
          console.error('[Auto-Recovery] Orphaned user message recovery error:', err);
          // Mark the assistant message with error on exception
          messages.updateOptimisticMessage(activeChat.id, newAssistantId, {
            content: '',
            isThinking: false,
            errorMessage: t(ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE),
            errorCode: ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE,
            isDifyAgentError: true,
          });
        });
      }
    });
  }, [activeChat, actions, messages]);

  // 2. Compose the final return value, wiring up inter-hook dependencies
  
  // Create coordinated handlers
  const handleReplyToMessage = useCallback((message: Message) => {
      editing.handleCancelEdit(); // Cancel edit when replying
      ui.handleReplyToMessage(message);
  }, [editing.handleCancelEdit, ui.handleReplyToMessage]);

  const cancelReply = useCallback(() => {
      ui.cancelReply();
      editing.handleCancelEdit(); // Also cancel edit when canceling reply
  }, [ui.cancelReply, editing.handleCancelEdit]);

  return {
    ...sessions,
    ...messages,
    ...actions,
    ...ui,
    ...files,
    handleFileUpload,
    ...editing,
    activeChat,
    
    // New state for handling initial message from outside the chat
    initialMessage,
    setInitialMessage,

    // Overridden handlers
    handleReplyToMessage,
    cancelReply,

    // Combined loading state
    isLoading: sessions.isLoading || sessions.isUserLoading,
  };
}

export type ChatController = ReturnType<typeof useChat>;
